import { useMemo } from 'react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { maskDecimal, maskWholeNumber } from '@givebutter/util';
import {
    IconDefinition,
    faBuildingUser,
    faMessagesQuestion,
    faUser,
    faUserGroup,
    faUsers,
    faUsersMedical,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PlanRowLabel } from 'assets/js/pages/account-settings/account-about/givebutter-plus/PlanRowLabel';
import { useSubscriptionPlans } from 'assets/js/pages/account-settings/account-about/givebutter-plus/useSubscriptionPlans';
import * as Table from 'components/table';
import { posthogEvents } from 'constants/posthog-events';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { usePosthog } from 'customHooks/usePosthog';
import { GivebutterPlusPlan } from 'types/domain';

const ICONS_MAP: IconDefinition[] = [faUser, faUserGroup, faUsers, faUsersMedical, faBuildingUser];

const columnHelper = createColumnHelper<GivebutterPlusPlan>();

interface UseSubscriptionFormTableOptions {
    showAnnualPricing: boolean;
    defaultPlanId: string;
    currentPlanId?: string;
}
const useSubscriptionFormTable = ({
    showAnnualPricing,
    defaultPlanId,
    currentPlanId,
}: UseSubscriptionFormTableOptions) => {
    const { data: plans } = useSubscriptionPlans();
    const { pushEvent } = usePosthog();

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: Table.Constants.Select,
                cell: info => (
                    <>
                        <PlanRowLabel row={info.row.original} currentPlanId={currentPlanId} />
                        <Table.RowRadioInput />
                    </>
                ),
                meta: {
                    ignoreLink: true,
                    className: 'relative',
                },
            }),
            columnHelper.display({
                header: 'Contacts',
                cell: info => {
                    const row = info.row.original;
                    const isLastRow = info.row.index === info.table.getRowModel().rows.length - 1;
                    return (
                        <>
                            <div className="flex h-8 w-8 items-center">
                                <FontAwesomeIcon
                                    icon={ICONS_MAP[info.row.index] ?? faMessagesQuestion}
                                    className="text-brand-violet-primary"
                                />
                            </div>
                            <span className="text-sm font-medium">
                                Up to {maskWholeNumber(row.contacts_max)}
                                {isLastRow && '+'}
                            </span>
                        </>
                    );
                },
            }),
            columnHelper.display({
                header: 'Monthly price',
                cell: info => {
                    const row = info.row.original;
                    return (
                        <div className="flex min-w-28 items-baseline gap-1">
                            <div className="text-sm font-medium text-gray-600">
                                <CurrencySymbol />
                                {maskDecimal(
                                    showAnnualPricing
                                        ? row.annualized_monthly_price
                                        : row.monthly_price,
                                )}
                            </div>
                            {showAnnualPricing && (
                                <div className="text-xs text-gray-500 line-through">
                                    <CurrencySymbol />
                                    {maskDecimal(row.monthly_price)}
                                </div>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.display({
                header: 'Extra contacts*',
                cell: info => {
                    const row = info.row.original;

                    return (
                        row.monthly_price && (
                            <div className="text-xs text-gray-500">
                                <span className="text-sm text-gray-600">
                                    + <CurrencySymbol />
                                    {maskDecimal(row.extra_contact_price_monthly)}
                                </span>{' '}
                                per 100
                            </div>
                        )
                    );
                },
            }),
        ],
        [showAnnualPricing, currentPlanId],
    );

    const table = useReactTable({
        data: plans,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: row => row.id,
        enableMultiRowSelection: false,
        enableHiding: false,
        enablePinning: false,
        meta: {
            onRowClick: (row, e) => {
                if (!row.getIsSelected()) {
                    row.getToggleSelectedHandler()(e);
                } else {
                    pushEvent(posthogEvents.DASHBOARD_BILLING_PLAN_SELECTED, {
                        billingCycle: showAnnualPricing ? 'year' : 'month',
                        planId: row.id,
                    });
                }
            },
            selectedRowClassName:
                '[&>*]:bg-blue-50 [&_td]:focus-within:bg-blue-50 [&_td]:hover:bg-blue-50',
        },
        initialState: {
            rowSelection: {
                [defaultPlanId]: true,
            },
        },
    });

    return { table };
};

export { useSubscriptionFormTable };
